import { cn } from "@/lib/utils";
import { NoImage } from "@assets/constant/index.constant";
import { Switch } from "@components/ui/switch";
import { endpoint } from "@services/api.services";
import Tooltip from "@ui/external/components/tooltip";
import { ZoomImage } from "@ui/internal/components/formelement";
import { isNullCheck, showToast } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { useSelectionStore } from "@utils/states/index.state";
import { formatDistanceToNow, isPast, parseISO } from "date-fns";
import { ClockIcon, CopyIcon } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";
interface CloumnListProps {
  refetch?: () => void;
  toggleActiveStatus: (id: string | number, state: boolean) => void;
  toggleSkippable: (id: string, state: boolean) => void;
}
const ColumnList = ({
  refetch,
  toggleActiveStatus,
  toggleSkippable,
}: CloumnListProps) => {
  const { clearSelection } = useSelectionStore();
  const { handleAction } = useHandleAction({
    endpoint: endpoint["popup"],
    refetch,
  });
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row: any) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },
      {
        name: <span className="flex items-center gap-2">Name</span>,
        cell: (row: any) => (
          <div className="flex items-center gap-3 w-full justify-between">
            <ZoomImage>
              <div className="border rounded-md hover:outline-[#1aa178]/50 hover:outline-2 overflow-hiddenbg-slate-100">
                <img
                  src={row?.image || NoImage}
                  alt="Cropped"
                  onError={(e: any) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = NoImage;
                  }}
                  className="object-cover size-12 border rounded-md"
                />
              </div>
            </ZoomImage>
            <Tooltip description={row?.name}>
              <div className="block w-fit max-w-[100px] truncate">
                {isNullCheck(row?.name) as string}
              </div>
            </Tooltip>
          </div>
        ),
        sortable: false,
      },
      {
        name: "Link",
        cell: (row: any) => (
          <>
            {row?.link ? (
              <Tooltip description={row?.link}>
                <Link to={row?.link} className="truncate max-w-[150px] w-fit">
                  {row?.link}
                </Link>
              </Tooltip>
            ) : (
              "N/A"
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: "Duration (In Seconds)",
        cell: (row: any) => (
          <span className="flex items-center gap-2">
            <ClockIcon size={14} />
            {row?.duration_in_seconds} seconds
          </span>
        ),
        sortable: true,
      },
      {
        name: "Is Show",
        cell: (row: any) => (
          <Tooltip description={"Toggle Visibility Status"}>
            <div>
              {" "}
              <Switch
                checked={row?.is_show}
                onCheckedChange={(value) => toggleActiveStatus(row?.id, value)}
                className="cursor-pointer"
              />
            </div>
          </Tooltip>
        ),
      },
      {
        name: "Is Skipable",
        cell: (row: any) => (
          <Tooltip description={"Toggle Skipable Status"}>
            <div>
              {" "}
              <Switch
                checked={row?.is_skipable}
                onCheckedChange={(value) => toggleSkippable(row?.id, value)}
                className="cursor-pointer"
              />
            </div>
          </Tooltip>
        ),
      },
      {
        name: "Actions",
        cell: (row: any) => {
          return (
            <div className="flex w-full items-center justify-start gap-4 cursor-pointer font-[500] select-none">
              <button
                className="text-[#1DA077] cursor-pointer"
                onClick={() =>
                  handleAction({ type: "modal-edit", data: row.id })
                }
                title="Edit"
              >
                Edit
              </button>
              <button
                className="text-[#C33241] cursor-pointer"
                onClick={() => {
                  clearSelection();
                  handleAction({
                    type: "confirm",
                    data: { type: "delete", id: row.id },
                  });
                }}
                title="Delete"
              >
                Delete
              </button>
            </div>
          );
        },
        sortable: false,
      },
    ],
    [handleAction, clearSelection, toggleActiveStatus]
  );

  return columns;
};

export { ColumnList };
